import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, ArrowRight, CheckCircle2 } from "lucide-react";
import Logo from "@/components/Logo";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/auth";

/**
 * CC-064 (2026-09-25) — Forgot Password entry point.
 *
 * The user enters their email; the backend acknowledges with a bland
 * message regardless of whether the account exists (privacy — no
 * enumeration leak). If the account IS real + active, a Resend-
 * delivered email lands with a single-use, 1-hour reset link.
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="cc-landing min-h-screen flex flex-col" data-testid="forgot-password-page">
      <div className="max-w-[1200px] w-full mx-auto px-8 py-5 flex items-center justify-between">
        <Link to="/"><Logo withWordmark tone="dark" /></Link>
        <Link to="/login" className="text-[12.5px] text-[color:var(--cc-navy-muted)] hover:text-white">
          ← Back to sign-in
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-[440px]">
          <div className="text-[11px] uppercase tracking-[.18em] text-[#ffc72c] font-semibold mb-3">
            Password reset
          </div>
          <h1 className="font-display font-extrabold text-[34px] leading-tight tracking-tight text-white">
            Reset your password.
          </h1>
          <p className="text-[13.5px] text-[color:var(--cc-navy-muted)] mt-2">
            Enter the email address on your CurrentControl account. If it matches an active account, we'll send you a one-time link.
          </p>

          {sent ? (
            <div
              className="mt-6 bg-[color:var(--cc-navy-card)]/70 backdrop-blur border border-[color:var(--cc-navy-border)] rounded-2xl p-6"
              data-testid="forgot-password-sent"
            >
              <div className="flex items-start gap-3">
                <CheckCircle2 size={20} className="text-[#7dd3a4] shrink-0 mt-[2px]" />
                <div>
                  <div className="text-white text-[14.5px] font-semibold">
                    Check your inbox.
                  </div>
                  <p className="text-[color:var(--cc-navy-muted)] text-[13px] mt-1">
                    If an account exists for <span className="font-mono-data text-white">{email}</span>, a reset link is on its way. The link expires in 1 hour and can only be used once.
                  </p>
                  <Link
                    to="/login"
                    className="inline-flex items-center gap-1 mt-4 text-[12.5px] text-[#4aa4ff] hover:underline"
                    data-testid="forgot-password-back-to-login"
                  >
                    ← Back to sign-in
                  </Link>
                </div>
              </div>
            </div>
          ) : (
            <form
              onSubmit={submit}
              className="mt-6 bg-[color:var(--cc-navy-card)]/70 backdrop-blur border border-[color:var(--cc-navy-border)] rounded-2xl p-6"
              data-testid="forgot-password-form"
            >
              <label className="block text-[11px] uppercase tracking-[.12em] text-[color:var(--cc-navy-muted)] font-semibold mb-2">
                Email
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="forgot-password-email-input"
                className="w-full bg-[#0a0f1a] border border-[color:var(--cc-navy-border)] rounded-lg px-3.5 py-3 text-[14px] text-white outline-none focus:border-[#0072bc] transition-colors"
                placeholder="you@franchise.com"
              />

              {err && (
                <div
                  className="mt-4 flex items-start gap-2 text-[12.5px] text-[#ff9d9d] bg-[#3a1414] border border-[#5c1e1e] rounded-md px-3 py-2"
                  data-testid="forgot-password-error"
                >
                  <AlertCircle size={14} strokeWidth={1.8} className="mt-[1px] shrink-0" />
                  {err}
                </div>
              )}

              <button
                type="submit"
                disabled={busy}
                data-testid="forgot-password-submit-btn"
                className="cc-btn-primary w-full justify-center mt-6 disabled:opacity-60"
              >
                {busy ? "Sending…" : (<>Send reset link <ArrowRight size={16} strokeWidth={1.8} /></>)}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
